// Trades a verified Google token for a four-hour agent session.
//
// This lives in its own module rather than in AgentMonitoringUtils for two reasons. It is
// imported by auth.js, which is in the eager entry graph via App.js, and AgentMonitoringUtils
// statically pulls in the mock fixtures (mockAgentData -> mockKnowledgeData, ~42 KB) that
// otherwise sit behind the lazy route import in Routes.js - so importing it from auth.js moved
// all of that onto the login page. It also uses a bare axios call, so it cannot be affected by
// the request interceptor that prefers the very session token this is trying to replace.
//
// Google's access token lasts about an hour and cannot be extended from the browser, which is
// what made the dashboard re-prompt hourly. The session token issued here lasts four hours and
// is what every later request presents.

import axios from 'axios';
import { AGENT_API_BASE } from './constants';
import {
    clearAgentSessionToken,
    getAgentSessionToken,
    getGoogleAccessToken,
    setAgentSessionToken
} from './googleAccessToken';

/**
 * Exchanges the current Google token for a session token, replacing any existing one.
 *
 * Best-effort: on failure the app carries on with the Google token exactly as before, so an
 * agent build without this endpoint still works, just with the shorter window.
 *
 * The existing session token is cleared first. When the stored token is itself the reason
 * calls are failing - the agent's signing secret rotated, the clock skewed - keeping it would
 * make the exchange fail for the same reason as everything else, and the "Re-authenticate"
 * button could never recover. It also matters because /login is unguarded: signing in as a
 * second person without logging out would otherwise leave the first person's token in place
 * and attribute their sends, newsletters and acknowledgements to the wrong operator.
 */
// Short on purpose. This runs in front of the dashboard's first data fetch, which has its own
// 20s timeout, so a cold or unreachable agent used to mean ~40s of "Loading..." before anything
// appeared. The exchange is best-effort with the Google token as a working fallback, so waiting
// longer buys nothing a user would notice and costs the thing they do notice.
const EXCHANGE_TIMEOUT_MS = 5000;

// How long to stop retrying after a failed exchange.
//
// Without this, a failure stores no token, so every mount and every Refresh click re-fires -
// which is exactly when the agent is already struggling, and the agent now rate-limits this
// endpoint. The retry storm and the 429 feed each other.
const FAILURE_BACKOFF_MS = 60000;

let inFlight = null;
let failedUntilMs = 0;

export async function startAgentSession() {
    clearAgentSessionToken();
    const googleToken = getGoogleAccessToken();
    if (!AGENT_API_BASE || !googleToken) {
        return false;
    }
    try {
        const response = await axios.post(
            `${AGENT_API_BASE}/admin/session`,
            {},
            { headers: { Authorization: `Bearer ${googleToken}` }, timeout: EXCHANGE_TIMEOUT_MS }
        );
        const { sessionToken, expiresAtMs } = response.data ?? {};
        if (typeof sessionToken === 'string' && typeof expiresAtMs === 'number') {
            setAgentSessionToken(sessionToken, expiresAtMs);
            return true;
        }
    } catch {
        // Older agent build, or unreachable. The Google token still authenticates.
    }
    return false;
}

/**
 * Obtains a session token if there is not already a usable one.
 *
 * Called on mount as well as at login, because login is not the only way to arrive holding a
 * Google token but no session. Anyone already signed in when this ships has one and not the
 * other, and a reload would never have obtained one; so would anyone whose first exchange lost
 * a race with an agent cold start. Both cases silently fall back to the one-hour Google token,
 * which is the exact re-prompting this was written to stop.
 */
export async function ensureAgentSession() {
    if (getAgentSessionToken()) {
        return true;
    }
    // One exchange at a time per tab. Several components mount together and each would
    // otherwise POST its own, multiplying load on the endpoint that is already rate-limited.
    if (inFlight !== null) {
        return inFlight;
    }
    if (Date.now() < failedUntilMs) {
        return false;
    }
    inFlight = startAgentSession()
        .then((ok) => {
            if (!ok) {
                failedUntilMs = Date.now() + FAILURE_BACKOFF_MS;
            }
            return ok;
        })
        .finally(() => {
            inFlight = null;
        });
    return inFlight;
}

/** Test seam: clears the in-flight promise and the failure backoff. */
export function resetAgentSessionState() {
    inFlight = null;
    failedUntilMs = 0;
}
